/**
 * Growth Engine — additional panels (Phases 3, 4, 5).
 *
 * Combined into a single module so the Marketing.jsx import list stays
 * lean. Each panel is exported individually:
 *   • AiStudioPanel       (Phase 3 — Gemini content generation)
 *   • IntelligencePanel   (Phase 3 — Industry + Opportunity Predictor)
 *   • LeadCapturePanel    (Phase 4 — channel registry + credential capture)
 *   • EventsPanel         (Phase 5 — events & exhibitions)
 *   • AssetsPanel         (Phase 5 — marketing assets library)
 *   • ReferralsPanel      (Phase 5 — referral program)
 */
import { useEffect, useState, useCallback } from "react";
import api, { fmtError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import OutlookMarketingCard from "./OutlookMarketingCard";
import { Sparkles, Copy, BookOpen, BarChart3, Lightbulb, IndianRupee, AlertTriangle, Users, Calendar, FolderOpen, Gift, Plus, Trash2, Plug, ExternalLink, Send } from "lucide-react";

const fmtINR = (n) => {
  const v = Number(n) || 0;
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(2)} L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(1)} k`;
  return `₹${v.toFixed(0)}`;
};
const cap = (s) => (s || "").replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

// ════════════════════════════════════════════════════════════════════
// AI STUDIO
// ════════════════════════════════════════════════════════════════════

export function AiStudioPanel() {
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({ content_type: "email", brief: "", industry: "", product: "", tone: "confident" });
  const [output, setOutput] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try { const { data } = await api.get("/marketing/studio/history"); setHistory(data || []); }
    catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    api.get("/marketing/studio/meta").then(({ data }) => setMeta(data));
    loadHistory();
  }, [loadHistory]);

  const generate = async () => {
    if (!form.brief.trim()) { toast.error("Tell the AI what you want — fill the brief"); return; }
    setBusy(true); setOutput(null);
    try {
      const { data } = await api.post("/marketing/studio/generate", form);
      setOutput(data);
      loadHistory();
      toast.success(`${cap(form.content_type)} generated (${data.word_count} words)`);
    } catch (e) { toast.error(fmtError(e)); }
    finally { setBusy(false); }
  };

  const copyText = () => {
    if (!output?.text) return;
    navigator.clipboard.writeText(output.text);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="grid lg:grid-cols-2 gap-5" data-testid="ai-studio-panel">
      {/* Input panel */}
      <Card className="rounded-3xl border-border/60 shadow-soft">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <div className="h-9 w-9 rounded-2xl bg-primary text-primary-foreground grid place-items-center"><Sparkles className="h-4 w-4" /></div>
            <div>
              <p className="font-display text-lg font-extrabold">AI Marketing Studio</p>
              <p className="text-[11px] text-muted-foreground">Powered by Gemini 3 Pro</p>
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold">Content type</Label>
            <Select value={form.content_type} onValueChange={(v) => setForm({ ...form, content_type: v })}>
              <SelectTrigger className="rounded-2xl h-11 mt-1" data-testid="studio-type"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-2xl max-h-72">
                {(meta?.content_types || []).map((t) => <SelectItem key={t} value={t} className="capitalize">{t.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-bold">Brief — what do you want to say?</Label>
            <Textarea rows={4} className="rounded-2xl mt-1" value={form.brief} onChange={(e) => setForm({ ...form, brief: e.target.value })} placeholder="e.g. Announce the new Ultraprobe 15000 leak detection service to cement plants in South India. Mention 30% energy savings." data-testid="studio-brief" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold">Target industry</Label>
              <Input className="rounded-2xl h-11 mt-1" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} placeholder="Cement / Steel / Power" />
            </div>
            <div>
              <Label className="text-xs font-bold">Featured product</Label>
              <Input className="rounded-2xl h-11 mt-1" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} placeholder="Ultraprobe 15000" />
            </div>
            <div>
              <Label className="text-xs font-bold">Tone</Label>
              <Select value={form.tone} onValueChange={(v) => setForm({ ...form, tone: v })}>
                <SelectTrigger className="rounded-2xl h-11 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta?.tones || ["confident"]).map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold">Language</Label>
              <Input className="rounded-2xl h-11 mt-1" value={form.language || "english"} onChange={(e) => setForm({ ...form, language: e.target.value })} />
            </div>
          </div>
          <Button onClick={generate} disabled={busy} className="rounded-2xl w-full" data-testid="studio-generate">
            <Sparkles className="h-4 w-4 mr-1.5" /> {busy ? "Generating…" : "Generate content"}
          </Button>
        </CardContent>
      </Card>

      {/* Output panel */}
      <Card className="rounded-3xl border-border/60 shadow-soft">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Generated content</p>
            {output && <Button variant="outline" size="sm" className="rounded-xl" onClick={copyText} data-testid="studio-copy"><Copy className="h-3 w-3 mr-1" />Copy</Button>}
          </div>
          {busy ? <Skeleton className="h-72 w-full rounded-2xl" /> : output ? (
            <>
              <Badge variant="outline" className="rounded-full text-[10px]">{output.word_count} words · {output.char_count} chars</Badge>
              <Textarea rows={16} className="rounded-2xl font-mono text-sm" value={output.text} onChange={(e) => setOutput({ ...output, text: e.target.value })} data-testid="studio-output" />
            </>
          ) : (
            <p className="text-sm text-muted-foreground py-12 text-center">Fill the brief and hit <strong>Generate content</strong> to get AI-written copy ready to paste into a campaign.</p>
          )}
        </CardContent>
      </Card>

      {/* History */}
      {history.length > 0 && (
        <Card className="lg:col-span-2 rounded-3xl border-border/60 shadow-soft">
          <CardContent className="p-5 space-y-2">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Recent generations</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {history.slice(0, 6).map((h) => (
                <div key={h.id} className="rounded-xl border border-border/60 p-2.5 hover:bg-muted/30 cursor-pointer" onClick={() => setOutput(h.output)}>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-[10px] capitalize">{h.input?.content_type?.replace(/_/g, " ")}</Badge>
                    <span className="text-[10px] text-muted-foreground">{new Date(h.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2">{h.input?.brief}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// INTELLIGENCE (Industries + Opportunity Predictor)
// ════════════════════════════════════════════════════════════════════

export function IntelligencePanel() {
  const [industries, setIndustries] = useState(null);
  const [opps, setOpps] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/marketing/intelligence/industries").then(({ data }) => setIndustries(data)),
      api.get("/marketing/intelligence/opportunities").then(({ data }) => setOpps(data)),
    ]).catch((e) => toast.error(fmtError(e))).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-5" data-testid="intelligence-panel">
      {/* Industry table + AI insights */}
      <Card className="rounded-3xl border-border/60 shadow-soft">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-2xl bg-sky-100 text-sky-700 grid place-items-center"><BarChart3 className="h-4 w-4" /></div>
            <div>
              <p className="font-display text-lg font-extrabold">Industry Intelligence</p>
              <p className="text-[11px] text-muted-foreground">Win-rate, revenue, sales-cycle by industry</p>
            </div>
          </div>
          {industries?.ai_insights && (
            <div className="rounded-2xl bg-primary/5 border border-primary/20 p-3">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary mb-1.5 flex items-center gap-1"><Lightbulb className="h-3 w-3" /> AI insights</p>
              <p className="text-sm whitespace-pre-line leading-relaxed">{industries.ai_insights}</p>
            </div>
          )}
          {loading ? <Skeleton className="h-40 w-full rounded-2xl" /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b">
                  <th className="py-2 font-bold">Industry</th>
                  <th className="py-2 text-right">Deals</th>
                  <th className="py-2 text-right">Won</th>
                  <th className="py-2 text-right">Win rate</th>
                  <th className="py-2 text-right">Revenue</th>
                  <th className="py-2 text-right">Avg deal</th>
                  <th className="py-2 text-right">Cycle (days)</th>
                </tr></thead>
                <tbody>
                  {(industries?.rows || []).map((r) => (
                    <tr key={r.industry} className="border-b last:border-0 hover:bg-muted/20">
                      <td className="py-2.5 font-semibold capitalize">{r.industry.replace(/_/g, " ")}</td>
                      <td className="py-2.5 text-right">{r.deals}</td>
                      <td className="py-2.5 text-right">{r.won}</td>
                      <td className="py-2.5 text-right">{r.win_rate}%</td>
                      <td className="py-2.5 text-right font-bold text-primary">{fmtINR(r.revenue)}</td>
                      <td className="py-2.5 text-right">{fmtINR(r.avg_deal_size)}</td>
                      <td className="py-2.5 text-right">{r.avg_cycle_days ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Opportunity Predictor */}
      <Card className="rounded-3xl border-border/60 shadow-soft">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center"><Sparkles className="h-4 w-4" /></div>
            <div>
              <p className="font-display text-lg font-extrabold">Opportunity Predictor</p>
              <p className="text-[11px] text-muted-foreground">AMC renewals, dormant accounts, repeat-buyer candidates</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <OppTile icon={IndianRupee} label="AMC due (45d)"      value={opps?.totals?.amc_due_45d}        tone="amber" />
            <OppTile icon={AlertTriangle} label="Dormant clients"  value={opps?.totals?.dormant_clients}    tone="rose" />
            <OppTile icon={Users} label="Repeat candidates"        value={opps?.totals?.repeat_candidates}  tone="emerald" />
            <OppTile icon={Sparkles} label="Hot leads"             value={opps?.totals?.hot_leads}          tone="primary" />
          </div>
          {opps?.totals?.predicted_repeat_revenue > 0 && (
            <p className="text-sm">
              Predicted repeat revenue (next 12 mo): <strong className="text-primary">{fmtINR(opps.totals.predicted_repeat_revenue)}</strong>
            </p>
          )}

          {/* AMC renewals list */}
          {(opps?.amc_due_45d || []).length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mt-2 mb-1.5">AMC renewals due in next 45 days</p>
              <ul className="space-y-1">
                {opps.amc_due_45d.slice(0, 10).map((c) => (
                  <li key={c.id} className="text-sm flex items-center justify-between border-b border-border/40 pb-1.5">
                    <span><strong>{c.company_name || c.name}</strong> <span className="text-muted-foreground">· {c.industry || "—"}</span></span>
                    <span className="text-xs text-amber-700 font-mono">{c.amc_end_date?.slice(0, 10)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Repeat buyer candidates */}
          {(opps?.repeat_candidates || []).length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mt-2 mb-1.5">Top repeat-buyer candidates</p>
              <ul className="space-y-1">
                {opps.repeat_candidates.slice(0, 10).map((r) => (
                  <li key={r.client_id} className="text-sm flex items-center justify-between border-b border-border/40 pb-1.5">
                    <span><strong>{r.name}</strong> <span className="text-muted-foreground">· {r.prior_wins} prior wins · {r.industry || "—"}</span></span>
                    <span className="text-xs"><span className="text-emerald-700 font-bold">{fmtINR(r.predicted_revenue)}</span> <span className="text-muted-foreground">predicted</span></span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function OppTile({ icon: Icon, label, value, tone }) {
  const TONE = {
    primary: "bg-primary/10 text-primary border-primary/30",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    rose:    "bg-rose-50 text-rose-700 border-rose-200",
  };
  return (
    <div className={`rounded-2xl p-3 border ${TONE[tone]}`}>
      <Icon className="h-4 w-4 mb-1" />
      <p className="text-[10px] uppercase tracking-[0.15em] font-bold opacity-80">{label}</p>
      <p className="font-display text-2xl font-extrabold mt-0.5">{value ?? "—"}</p>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// LEAD CAPTURE — Phase 4 (channel registry; SDK integrations pending)
// ════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════
// GMAIL BULK MARKETING (OAuth connect + composer)
// ════════════════════════════════════════════════════════════════════

function GmailMarketingCard() {
  const [status, setStatus] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [testError, setTestError] = useState("");
  const [busy, setBusy] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", html: "", recipients: "" });
  const [batches, setBatches] = useState([]);

  const load = useCallback(async () => {
    try {
      const [{ data: s }, { data: b }] = await Promise.all([
        api.get("/marketing/gmail/status"),
        api.get("/marketing/gmail/batches", { params: { limit: 5 } }),
      ]);
      setStatus(s);
      setBatches(b || []);
    } catch { setStatus({ configured: false }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Live-refresh batch progress while any batch is running
  useEffect(() => {
    if (!batches.some((b) => b.status === "running")) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [batches, load]);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/marketing/gmail/auth-url", { params: { origin: window.location.origin } });
      window.location.href = data.url;
    } catch (e) { toast.error(fmtError(e)); setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect the Gmail account? Bulk email sending will stop working.")) return;
    try { await api.post("/marketing/gmail/disconnect"); toast.success("Gmail disconnected"); load(); }
    catch (e) { toast.error(fmtError(e)); }
  };

  const testSend = async () => {
    setBusy(true);
    setTestError("");
    try {
      await api.post("/marketing/gmail/test-send", { to: testTo });
      toast.success(`Test email sent to ${testTo} — check the inbox!`, { duration: 6000 });
      setTestTo(""); load();
    } catch (e) {
      let msg;
      try { msg = fmtError(e); } catch { msg = "Send failed"; }
      msg = String(msg || "Send failed");
      console.error("gmail test-send failed:", e);
      setTestError(msg);  // inline — survives even if the toast layer misbehaves
      toast.error(msg, { duration: 8000 });
    } finally { setBusy(false); }
  };

  const loadContactEmails = async () => {
    try {
      const { data } = await api.get("/marketing-contacts");
      const emails = [...new Set((data || []).map((c) => (c.email || "").trim().toLowerCase()).filter((e) => e && e.includes("@")))];
      if (!emails.length) { toast.info("No marketing contacts with email addresses found"); return; }
      setForm((f) => ({ ...f, recipients: emails.join("\n") }));
      toast.success(`${emails.length} email addresses loaded from Marketing Contacts`);
    } catch (e) { toast.error(fmtError(e)); }
  };

  const sendBulk = async () => {
    const recipients = form.recipients.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);
    if (!recipients.length) { toast.error("Add at least one recipient email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/marketing/gmail/send-bulk", {
        subject: form.subject, html: form.html.replace(/\n/g, "<br/>"), recipients,
      });
      toast.success(`Bulk send started — ${data.total} emails queued`);
      setComposeOpen(false);
      setForm({ subject: "", html: "", recipients: "" });
      load();
    } catch (e) { toast.error(fmtError(e)); } finally { setBusy(false); }
  };

  if (!status) return <Skeleton className="h-32 rounded-3xl" />;

  return (
    <Card className="rounded-3xl border-emerald-200/70 shadow-soft" data-testid="gmail-marketing-card">
      <CardContent className="p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="h-10 w-10 rounded-2xl bg-emerald-50 text-emerald-700 grid place-items-center"><Send className="h-4 w-4" /></div>
          <div className="flex-1 min-w-0">
            <p className="font-display text-base font-extrabold">Gmail — Bulk Marketing Email</p>
            <p className="text-xs text-muted-foreground truncate">
              {status.connected
                ? <>Connected as <strong>{status.email}</strong> · {status.sent_today}/{status.daily_limit} sent today</>
                : status.configured ? "Credentials configured — connect the Google account to go live" : "Server credentials missing"}
            </p>
          </div>
          <Badge variant="outline" className={`text-[10px] rounded-full ${status.connected ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-border"}`} data-testid="gmail-status-badge">
            {status.connected ? "Live" : "Not connected"}
          </Badge>
        </div>

        {!status.connected ? (
          <>
            <Button onClick={connect} disabled={busy || !status.configured} className="rounded-2xl mt-3 w-full sm:w-auto" data-testid="gmail-connect-btn">
              <ExternalLink className="h-4 w-4 mr-1.5" /> {busy ? "Redirecting…" : "Connect Google account"}
            </Button>
            <div className="mt-3 p-3 rounded-2xl border border-amber-200 bg-amber-50/60 text-xs space-y-1.5" data-testid="gmail-setup-help">
              <p className="font-bold text-amber-900">One-time Google Cloud Console setup (fixes “Error 400: redirect_uri_mismatch”):</p>
              <p className="text-amber-800">1. <strong>APIs &amp; Services → Credentials → your OAuth client → Authorized redirect URIs</strong> — add exactly:</p>
              <div className="flex items-center gap-1.5">
                <code className="flex-1 px-2 py-1.5 rounded-lg bg-white border border-amber-200 text-[11px] break-all select-all" data-testid="gmail-redirect-uri">
                  {`${window.location.origin}/api/marketing/gmail/callback`}
                </code>
                <Button
                  variant="outline" size="sm" className="rounded-xl h-8 shrink-0"
                  onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/api/marketing/gmail/callback`); toast.success("Redirect URI copied"); }}
                  data-testid="gmail-copy-redirect-uri"
                >
                  <Copy className="h-3 w-3 mr-1" /> Copy
                </Button>
              </div>
              <p className="text-amber-800">2. <strong>OAuth consent screen → Test users</strong> — add the Gmail address you'll connect with.</p>
              <p className="text-amber-800">3. <strong>APIs &amp; Services → Library</strong> — enable <strong>Gmail API</strong>. Then come back and hit Connect (Google can take a few minutes to apply changes).</p>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <Button onClick={() => setComposeOpen(true)} className="rounded-2xl" data-testid="gmail-compose-btn">
                <Send className="h-4 w-4 mr-1.5" /> Send bulk email
              </Button>
              <div className="flex items-center gap-1.5 flex-1 min-w-[220px]">
                <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="test@company.com" className="rounded-2xl h-10" data-testid="gmail-test-to" />
                <Button variant="outline" onClick={testSend} disabled={busy || !testTo} className="rounded-2xl h-10 shrink-0" data-testid="gmail-test-send">Test send</Button>
              </div>
              <Button variant="ghost" onClick={disconnect} className="rounded-2xl text-red-600 hover:bg-red-50" data-testid="gmail-disconnect">Disconnect</Button>
            </div>
            {testError && (
              <p className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2" data-testid="gmail-test-error">
                ⚠ {testError}
              </p>
            )}
            {batches.length > 0 && (
              <div className="mt-3 pt-3 border-t border-border/50 space-y-1.5">
                <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-muted-foreground">Recent batches</p>
                {batches.map((b) => (
                  <div key={b.id} data-testid={`gmail-batch-${b.id}`}>
                    <div className="flex items-center justify-between text-xs">
                      <span className="truncate flex-1">{b.subject}</span>
                      <span className={`ml-2 shrink-0 font-semibold ${b.status === "running" ? "text-amber-600" : (b.failed ? "text-red-600" : "text-emerald-700")}`}>
                        {b.sent}/{b.total} sent{b.failed ? ` · ${b.failed} failed` : ""}{b.status === "running" ? " · sending…" : ""}
                      </span>
                    </div>
                    {b.failed > 0 && b.last_error && (
                      <p className="text-[10px] text-red-600 mt-0.5 line-clamp-2" data-testid={`gmail-batch-error-${b.id}`}>
                        Reason: {(() => { try { const j = JSON.parse(b.last_error); return j?.error?.message || b.last_error; } catch { return b.last_error; } })()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
          <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="gmail-compose-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Bulk marketing email</DialogTitle>
              <p className="text-xs text-muted-foreground">Sends from {status.email}. {status.daily_limit - status.sent_today} sends left today.</p>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-bold">Subject *</Label>
                <Input className="rounded-2xl h-11 mt-1" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="gmail-subject" />
              </div>
              <div>
                <Label className="text-xs font-bold">Message *</Label>
                <Textarea className="rounded-2xl mt-1" rows={6} value={form.html} onChange={(e) => setForm({ ...form, html: e.target.value })} placeholder="Write your message… (line breaks preserved; HTML allowed)" data-testid="gmail-body" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-bold">Recipients * (one per line)</Label>
                  <button type="button" className="text-[11px] underline text-primary-700" onClick={loadContactEmails} data-testid="gmail-load-contacts">Load from Marketing Contacts</button>
                </div>
                <Textarea className="rounded-2xl mt-1 font-mono text-xs" rows={5} value={form.recipients} onChange={(e) => setForm({ ...form, recipients: e.target.value })} placeholder={"buyer@plant.com\nmaintenance@factory.in"} data-testid="gmail-recipients" />
                <p className="text-[10px] text-muted-foreground mt-1">{form.recipients.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean).length} recipients · duplicates & invalid emails are skipped automatically</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setComposeOpen(false)}>Cancel</Button>
              <Button onClick={sendBulk} disabled={busy || !form.subject || !form.html} data-testid="gmail-send-bulk">{busy ? "Starting…" : "Send to all"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export function LeadCapturePanel() {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creds, setCreds] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/marketing/lead-capture/channels"); setChannels(data || []); }
    catch (e) { toast.error(fmtError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // OAuth return toast (?gmail=connected / ?gmail=error)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const g = p.get("gmail");
    if (g === "connected") toast.success("Gmail connected — bulk marketing email is live!");
    else if (g === "error") toast.error(`Gmail connect failed: ${p.get("reason") || "unknown error"}`);
    if (g) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const openConnect = (ch) => { setEditing(ch); setCreds({}); };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/marketing/lead-capture/connect", { channel: editing.key, ...creds });
      toast[data.ok ? "success" : "info"](data.message);
      setEditing(null); load();
    } catch (e) { toast.error(fmtError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="lead-capture-panel">
      <OutlookMarketingCard />
      <Card className="rounded-3xl border-amber-200 bg-amber-50/40">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
          <div>
            <p className="font-bold text-amber-900">Lead Capture integrations are in skeleton phase.</p>
            <p className="text-sm text-amber-800 mt-1">You can save credentials per channel now. Live data fetching goes online channel-by-channel once we wire up the official SDKs — share API keys with the team and we'll switch each integration to "Live".</p>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="grid sm:grid-cols-2 gap-3">{[1,2,3,4].map((i) => <Skeleton key={i} className="h-32 rounded-3xl" />)}</div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {channels.filter((ch) => ch.key !== "gmail").map((ch) => (
            <Card key={ch.key} className="rounded-3xl border-border/60 shadow-soft" data-testid={`channel-${ch.key}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="h-9 w-9 rounded-2xl bg-muted grid place-items-center"><Plug className="h-4 w-4" /></div>
                  <div className="flex-1">
                    <p className="font-display text-base font-extrabold">{ch.label}</p>
                    <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{ch.key}</p>
                  </div>
                  <Badge variant="outline" className={`text-[10px] rounded-full ${ch.connected ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-border"}`}>
                    {ch.connected ? "Live" : "Not connected"}
                  </Badge>
                </div>
                <p className="text-[11px] text-muted-foreground mb-2">Requires: {ch.needs.join(", ")}</p>
                <Button onClick={() => openConnect(ch)} variant="outline" size="sm" className="rounded-xl w-full" data-testid={`connect-${ch.key}`}>
                  {ch.connected ? "Reconfigure" : "Connect / save credentials"}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="connect-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing?.label}</DialogTitle>
            <p className="text-xs text-muted-foreground">Paste the credentials below. They're stored encrypted-at-rest and never shown in any UI again.</p>
          </DialogHeader>
          {editing && (
            <div className="space-y-2">
              {editing.needs.map((field) => (
                <div key={field}>
                  <Label className="text-xs font-bold capitalize">{field.replace(/_/g, " ")}</Label>
                  <Input className="rounded-2xl h-11 mt-1" value={creds[field] || ""} onChange={(e) => setCreds({ ...creds, [field]: e.target.value })} data-testid={`cred-${field}`} />
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="connect-save">{saving ? "Saving…" : "Save credentials"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// EVENTS & EXHIBITIONS
// ════════════════════════════════════════════════════════════════════

const EVENT_TYPES = ["industrial_expo", "trade_fair", "seminar", "webinar", "vendor_meet", "training_program", "csr", "govt_expo"];

export function EventsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/marketing/events"); setRows(data || []); }
    catch (e) { toast.error(fmtError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (ev) => {
    if (!window.confirm(`Delete event "${ev.name}"?`)) return;
    try { await api.delete(`/marketing/events/${ev.id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(fmtError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="events-panel">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">{rows.length} events</p>
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="rounded-2xl" data-testid="event-new"><Plus className="h-4 w-4 mr-1.5" />New event</Button>
      </div>
      {loading ? <Skeleton className="h-40 w-full rounded-3xl" /> : rows.length === 0 ? (
        <EmptyState icon={Calendar} title="No events yet" body="Capture every industrial expo, seminar, or vendor meet to track leads + ROI per event." />
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {rows.map((ev) => (
            <Card key={ev.id} className="rounded-3xl border-border/60 shadow-soft" data-testid={`event-card-${ev.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div>
                    <p className="font-display text-lg font-extrabold">{ev.name}</p>
                    <p className="text-[11px] text-muted-foreground capitalize">{ev.event_type?.replace(/_/g, " ")} · {ev.location || "—"}</p>
                  </div>
                  <Badge variant="outline" className="text-[10px] capitalize rounded-full">{ev.status}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">{ev.start_date} → {ev.end_date}</p>
                <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                  <div><p className="text-[10px] uppercase font-bold text-muted-foreground">Leads</p><p className="font-bold">{ev.leads_captured ?? 0}</p></div>
                  <div><p className="text-[10px] uppercase font-bold text-muted-foreground">Budget</p><p className="font-bold">{fmtINR(ev.budget)}</p></div>
                  <div><p className="text-[10px] uppercase font-bold text-muted-foreground">Spend</p><p className="font-bold">{fmtINR(ev.actual_spend)}</p></div>
                </div>
                <div className="flex gap-2 mt-3 pt-2 border-t border-border/60">
                  <Button variant="outline" size="sm" className="rounded-xl flex-1" onClick={() => { setEditing(ev); setOpen(true); }}>Edit</Button>
                  <Button variant="ghost" size="icon" className="h-9 w-9 text-rose-600" onClick={() => remove(ev)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <SimpleCrudDialog
        open={open} onOpenChange={setOpen} title={editing ? "Edit event" : "New event"}
        initial={editing}
        emptyDoc={{ name: "", event_type: "industrial_expo", location: "", start_date: "", end_date: "", budget: 0, actual_spend: 0, expected_leads: 0, status: "planned", notes: "" }}
        fields={[
          ["name", "Name"],
          ["event_type", "Type", "select", EVENT_TYPES],
          ["location", "Location"],
          ["start_date", "Start date", "date"],
          ["end_date", "End date", "date"],
          ["budget", "Budget (₹)", "number"],
          ["actual_spend", "Actual spend (₹)", "number"],
          ["expected_leads", "Expected leads", "number"],
          ["status", "Status", "select", ["planned", "live", "completed", "cancelled"]],
          ["notes", "Notes", "textarea"],
        ]}
        onSubmit={async (doc) => {
          if (editing) await api.patch(`/marketing/events/${editing.id}`, doc);
          else        await api.post("/marketing/events", doc);
          load();
        }}
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// MARKETING ASSETS
// ════════════════════════════════════════════════════════════════════

const ASSET_CATEGORIES = ["brochure", "case_study", "datasheet", "video", "certificate", "presentation", "tender", "logo", "email_template", "proposal_template", "sop", "customer_testimonial"];

export function AssetsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("__all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = categoryFilter === "__all" ? {} : { category: categoryFilter };
      const { data } = await api.get("/marketing/assets", { params });
      setRows(data || []);
    } catch (e) { toast.error(fmtError(e)); } finally { setLoading(false); }
  }, [categoryFilter]);
  useEffect(() => { load(); }, [load]);

  const remove = async (a) => {
    if (!window.confirm(`Delete asset "${a.name}"?`)) return;
    try { await api.delete(`/marketing/assets/${a.id}`); load(); }
    catch (e) { toast.error(fmtError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="assets-panel">
      <div className="flex items-center gap-3 justify-between flex-wrap">
        <div className="flex items-center gap-2">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="rounded-2xl h-10 w-48" data-testid="asset-cat-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All categories</SelectItem>
              {ASSET_CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Badge variant="outline" className="rounded-full text-xs">{rows.length} assets</Badge>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-2xl" data-testid="asset-new"><Plus className="h-4 w-4 mr-1.5" />New asset</Button>
      </div>
      {loading ? <Skeleton className="h-40 w-full rounded-3xl" /> : rows.length === 0 ? (
        <EmptyState icon={FolderOpen} title="Marketing assets library" body="Centralise every brochure, case study, datasheet, video, certificate, and template here. Tag by industry & product so sales can find them in seconds." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((a) => (
            <Card key={a.id} className="rounded-3xl border-border/60 shadow-soft hover:shadow-md transition" data-testid={`asset-${a.id}`}>
              <CardContent className="p-4">
                <Badge variant="outline" className="text-[10px] capitalize rounded-full mb-2">{a.asset_category?.replace(/_/g, " ")}</Badge>
                <p className="font-display text-base font-extrabold line-clamp-1">{a.name}</p>
                {a.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{a.description}</p>}
                <div className="text-[11px] text-muted-foreground mt-2 space-y-0.5">
                  {a.industry && <p>Industry: <strong>{a.industry}</strong></p>}
                  {a.language && <p>Lang: {a.language} · {a.version}</p>}
                </div>
                {a.tags?.length > 0 && <div className="flex flex-wrap gap-1 mt-2">{a.tags.slice(0, 4).map((t) => <Badge key={t} variant="secondary" className="text-[9px]">{t}</Badge>)}</div>}
                <div className="flex gap-2 mt-3 pt-2 border-t border-border/60">
                  {a.file_url && <a href={a.file_url} target="_blank" rel="noreferrer" className="text-xs font-bold text-primary flex items-center gap-1"><ExternalLink className="h-3 w-3" /> Open</a>}
                  <div className="flex-1" />
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={() => remove(a)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <SimpleCrudDialog
        open={open} onOpenChange={setOpen} title="New asset"
        emptyDoc={{ name: "", asset_category: "brochure", description: "", industry: "", file_url: "", language: "english", version: "v1", tags: "" }}
        fields={[
          ["name", "Name"],
          ["asset_category", "Category", "select", ASSET_CATEGORIES],
          ["description", "Description", "textarea"],
          ["industry", "Industry"],
          ["file_url", "File URL"],
          ["language", "Language"],
          ["version", "Version"],
          ["tags", "Tags (comma separated)"],
        ]}
        onSubmit={async (doc) => {
          const payload = { ...doc, tags: (doc.tags || "").split(",").map((s) => s.trim()).filter(Boolean) };
          await api.post("/marketing/assets", payload);
          load();
        }}
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// REFERRALS
// ════════════════════════════════════════════════════════════════════

const REFERRER_KINDS = ["customer", "consultant", "architect", "contractor", "employee", "partner", "distributor"];
const REFERRAL_STATUSES = ["new", "engaged", "qualified", "converted", "lost", "reward_paid"];

export function ReferralsPanel() {
  const [data, setData] = useState({ rows: [], leaderboard: [] });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("__all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter === "__all" ? {} : { status: statusFilter };
      const { data: r } = await api.get("/marketing/referrals", { params });
      setData(r || { rows: [], leaderboard: [] });
    } catch (e) { toast.error(fmtError(e)); } finally { setLoading(false); }
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (r, status) => {
    try { await api.patch(`/marketing/referrals/${r.id}`, { status }); load(); toast.success("Updated"); }
    catch (e) { toast.error(fmtError(e)); }
  };
  const remove = async (r) => {
    if (!window.confirm("Delete this referral?")) return;
    try { await api.delete(`/marketing/referrals/${r.id}`); load(); }
    catch (e) { toast.error(fmtError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="referrals-panel">
      <div className="flex items-center gap-3 justify-between flex-wrap">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="rounded-2xl h-10 w-48" data-testid="ref-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All statuses</SelectItem>
            {REFERRAL_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={() => setOpen(true)} className="rounded-2xl" data-testid="ref-new"><Plus className="h-4 w-4 mr-1.5" />New referral</Button>
      </div>

      {data.leaderboard?.length > 0 && (
        <Card className="rounded-3xl border-border/60 shadow-soft bg-gradient-to-br from-primary/5 to-emerald-50/30">
          <CardContent className="p-5">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary mb-2 flex items-center gap-1"><Gift className="h-3 w-3" /> Referrer leaderboard</p>
            <div className="space-y-1.5">
              {data.leaderboard.map((l, i) => (
                <div key={l.name} className="flex items-center justify-between text-sm border-b border-border/40 pb-1.5 last:border-0">
                  <span><strong className="tabular-nums mr-2">#{i + 1}</strong> {l.name} <span className="text-[11px] text-muted-foreground capitalize">· {l.kind}</span></span>
                  <span className="text-xs"><strong>{l.converted}</strong> won / {l.referrals} total · <span className="text-primary font-bold">{fmtINR(l.revenue)}</span></span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? <Skeleton className="h-40 w-full rounded-3xl" /> : data.rows.length === 0 ? (
        <EmptyState icon={Gift} title="No referrals tracked yet" body="Log every customer / consultant / distributor referral. Track reward, conversion, and revenue per referrer to identify your top advocates." />
      ) : (
        <div className="space-y-2">
          {data.rows.map((r) => (
            <Card key={r.id} className="rounded-3xl border-border/60 shadow-soft" data-testid={`ref-card-${r.id}`}>
              <CardContent className="p-4 flex flex-col sm:flex-row gap-2 sm:items-center justify-between">
                <div>
                  <p className="font-semibold">{r.referred_lead_name} <span className="text-muted-foreground text-xs">· {r.referred_lead_company || "—"}</span></p>
                  <p className="text-[11px] text-muted-foreground">Referred by <strong>{r.referred_by_name}</strong> ({r.referred_by_kind}) · {new Date(r.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={r.status} onValueChange={(v) => updateStatus(r, v)}>
                    <SelectTrigger className="rounded-xl h-9 w-36" data-testid={`ref-status-${r.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{REFERRAL_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button variant="ghost" size="icon" className="h-9 w-9 text-rose-600" onClick={() => remove(r)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <SimpleCrudDialog
        open={open} onOpenChange={setOpen} title="New referral"
        emptyDoc={{ referred_by_kind: "customer", referred_by_name: "", referred_by_contact: "", referred_lead_name: "", referred_lead_company: "", referred_lead_phone: "", referred_lead_email: "", reward_type: "cash", reward_value: 0, status: "new", notes: "" }}
        fields={[
          ["referred_by_kind", "Referrer type", "select", REFERRER_KINDS],
          ["referred_by_name", "Referrer name"],
          ["referred_by_contact", "Referrer phone/email"],
          ["referred_lead_name", "Referred lead name"],
          ["referred_lead_company", "Lead's company"],
          ["referred_lead_phone", "Lead's phone"],
          ["referred_lead_email", "Lead's email"],
          ["reward_type", "Reward type", "select", ["cash", "discount", "gift", "none"]],
          ["reward_value", "Reward value (₹)", "number"],
          ["notes", "Notes", "textarea"],
        ]}
        onSubmit={async (doc) => { await api.post("/marketing/referrals", doc); load(); }}
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Shared bits
// ════════════════════════════════════════════════════════════════════

function EmptyState({ icon: Icon, title, body }) {
  return (
    <Card className="rounded-3xl"><CardContent className="p-10 text-center">
      <div className="h-14 w-14 rounded-3xl bg-primary/10 text-primary mx-auto grid place-items-center mb-3"><Icon className="h-6 w-6" /></div>
      <p className="font-semibold">{title}</p>
      <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">{body}</p>
    </CardContent></Card>
  );
}

/** Tiny config-driven CRUD dialog. Field tuple: [key, label, type?, options?] */
function SimpleCrudDialog({ open, onOpenChange, title, initial, emptyDoc, fields, onSubmit }) {
  const [doc, setDoc] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setDoc(initial ? { ...emptyDoc, ...initial } : { ...emptyDoc }); }, [open, initial]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    setSaving(true);
    try { await onSubmit(doc); toast.success("Saved"); onOpenChange(false); }
    catch (e) { toast.error(fmtError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl sm:max-w-md max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-xl">{title}</DialogTitle></DialogHeader>
        <div className="space-y-2.5">
          {fields.map(([key, label, type = "text", options]) => (
            <div key={key}>
              <Label className="text-xs font-bold">{label}</Label>
              {type === "textarea" ? (
                <Textarea rows={3} className="rounded-2xl mt-1" value={doc[key] || ""} onChange={(e) => setDoc({ ...doc, [key]: e.target.value })} />
              ) : type === "select" ? (
                <Select value={doc[key] || ""} onValueChange={(v) => setDoc({ ...doc, [key]: v })}>
                  <SelectTrigger className="rounded-2xl h-11 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{(options || []).map((o) => <SelectItem key={o} value={o} className="capitalize">{o.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input type={type} className="rounded-2xl h-11 mt-1" value={doc[key] ?? ""} onChange={(e) => setDoc({ ...doc, [key]: type === "number" ? Number(e.target.value) : e.target.value })} />
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
